/**
 * Build 5: a shared register read and saved row by row.
 *
 * Replaces `useEntered` (one note, the whole register, last save wins) for
 * the registers several people keep at once. The screen still holds the
 * register whole — `value`, `save(next)` — so its own edits do not change;
 * what changes is what a save sends: only the rows that differ from the
 * register the change was made FROM, each with the version that register was
 * read at. A colleague's change to another row is never touched; a
 * colleague's change to the same row is refused, and the screen says so and
 * shows theirs.
 *
 * On the platform, a register that has never held a row but whose old note
 * is there is offered across once — never moved silently. Until somebody
 * brings it across, the note's register is shown as it always was, and a
 * save asks for it to be brought across first.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useSessionIfAny } from "../../app/SessionContext";
import {
  isRowConflict,
  noteRows,
  rowChanges,
  type RegisterRow,
  type RegisterRows,
  type RegisterSpec,
} from "../../domain/registerRows";
import { t } from "../../i18n";

export interface RowConflictNotice {
  rowId: string;
  /** "the docking unit on stand 107" */
  what: string;
  by?: string;
  at?: string;
  deleted: boolean;
}

export type SaveOutcome = { ok: true } | { ok: false; text: string; conflict?: RowConflictNotice };

export interface RegisterDoc<D> {
  value: D;
  /** the rows the value was put together from */
  rows: RegisterRow[];
  /** who changed the register last, and when */
  by?: string;
  at?: string;
  loading: boolean;
  /** the rows could not be read */
  error: string | null;
  canWrite: boolean;
  /** the old note, on the platform, waiting to be brought across */
  waiting: { rows: number; by?: string; at?: string } | null;
  bringAcross: () => Promise<{ ok: boolean; text: string }>;
  /**
   * Save the register. `base` is the register the change was made from —
   * by default the one on screen now; an editor opened earlier passes the one
   * it opened on, so a colleague's save since is caught.
   */
  save: (next: D, base?: D) => Promise<SaveOutcome>;
  /** the same, throwing the refusal in words — for an editor that shows its own */
  commit: (next: D, base?: D) => Promise<void>;
  saving: boolean;
  savedTo: string | null;
  failed: string | null;
  conflict: RowConflictNotice | null;
  dismiss: () => void;
}

const rowsKey = (siteId: string, key: string) => ["register-rows", siteId, key] as const;

/** "Faisal changed equipment item GSE-12 after you opened it. …" */
export function conflictSentence(c: RowConflictNotice): string {
  const who = c.by ?? t("Someone else");
  return `${
    c.deleted
      ? t("{who} deleted {what} after you opened it.", { who, what: c.what })
      : t("{who} changed {what} after you opened it.", { who, what: c.what })
  } ${t("Your change was not saved. Close this and open it again to see theirs.")}`;
}

export function useRegisterDoc<D>(
  spec: RegisterSpec<D>,
  /** a reading laid over the register that is never saved (what the spec's rows leave out) — memoised by the caller */
  opts: { view?: (doc: D) => D } = {},
): RegisterDoc<D> {
  const view = opts.view;
  /* a bench with no session (a screen drawn on its own) reads nothing and saves nothing */
  const session = useSessionIfAny();
  const qc = useQueryClient();
  const source = session?.source;
  const siteId = session?.site.id ?? "none";
  const who = session?.principal.name ?? "";
  const platform = source?.kind === "platform";

  const q = useQuery({
    enabled: !!source,
    queryKey: rowsKey(siteId, spec.key),
    staleTime: 15_000,
    /* the platform's events refresh it sooner (LiveRefresh); this is the floor */
    refetchInterval: 60_000,
    queryFn: () => source!.listRegisterRows(siteId, spec.key),
  });

  /* the register kept the old way, as long as the platform has never held a row of it */
  const noteQ = useQuery({
    queryKey: ["register-note", siteId, spec.topic],
    enabled: platform && q.data?.ever === 0,
    staleTime: 60_000,
    queryFn: async () => {
      const notes = await source!.listNotes(siteId, spec.topic);
      const docs: { id: string; doc: D; by?: string; at: string }[] = [];
      for (const n of notes) {
        try {
          const doc = JSON.parse(n.body) as D;
          if (noteRows(spec, doc).length) docs.push({ id: n.id, doc, by: n.createdBy, at: n.createdAt });
        } catch {
          /* not a register */
        }
        /* the newest is the register, unless the register was every note merged */
        if (docs.length && !spec.mergeNotes) break;
      }
      const newest = docs[0];
      if (!newest) return null;
      const doc = spec.mergeNotes && docs.length > 1 ? spec.mergeNotes(docs.map((d) => d.doc)) : newest.doc;
      return { id: newest.id, rows: noteRows(spec, doc), by: newest.by, at: newest.at };
    },
  });
  const note = platform && q.data?.ever === 0 ? (noteQ.data ?? null) : null;

  const [pending, setPending] = useState<D | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [conflict, setConflict] = useState<RowConflictNotice | null>(null);
  /* the versions each register drawn on screen was read at — what a save made from it sends */
  const versionsOf = useRef(new WeakMap<object, Map<string, number>>());

  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const fromRows = useMemo(() => {
    const raw = note ? spec.fromRows(note.rows) : spec.fromRows(rows);
    const doc = view ? view(raw) : raw;
    versionsOf.current.set(doc as object, new Map(note ? [] : rows.map((r) => [r.id, r.version])));
    return doc;
  }, [rows, note, spec, view]);
  const value = pending ?? fromRows;

  const latest = rows.reduce<RegisterRow | undefined>((a, r) => (!a || r.updatedAt > a.updatedAt ? r : a), undefined);

  const refresh = () => void qc.invalidateQueries({ queryKey: rowsKey(siteId, spec.key) });

  const save = async (next: D, base: D = value): Promise<SaveOutcome> => {
    setFailed(null);
    if (!source) {
      const text = t("There is no platform session to save to.");
      setFailed(text);
      return { ok: false, text };
    }
    if (note) {
      const text = t("Bring the register across first, then make your change.");
      setFailed(text);
      return { ok: false, text };
    }
    const versions = versionsOf.current.get(base as object) ?? new Map(rows.map((r) => [r.id, r.version]));
    const ops = rowChanges(spec.toRows(base), spec.toRows(next), versions);
    if (!ops.length) return { ok: true };
    setPending(next);
    setSaving(true);
    try {
      const saved = await source.changeRegisterRows(siteId, spec.key, ops);
      qc.setQueryData<RegisterRows>(rowsKey(siteId, spec.key), (old) => {
        if (!old) return old;
        const byId = new Map(old.rows.map((r) => [r.id, r]));
        for (const r of saved) byId.set(r.id, r);
        const order = [...old.rows.map((r) => r.id), ...saved.filter((r) => !old.rows.some((o) => o.id === r.id)).map((r) => r.id)];
        return {
          ...old,
          ever: Math.max(old.ever, order.length),
          rows: order.map((id) => byId.get(id)!).filter((r) => !r.deletedAt),
        };
      });
      setConflict(null);
      setSavedTo(
        platform
          ? t("saved on the platform under {who}", { who })
          : t("Saved in this browser's sample."),
      );
      refresh();
      return { ok: true };
    } catch (e) {
      if (isRowConflict(e)) {
        const c: RowConflictNotice = {
          rowId: e.rowId,
          what: spec.describe(e.rowId, e.current?.data),
          by: e.kind === "row_deleted" ? e.current?.deletedBy : e.current?.updatedBy,
          at: e.kind === "row_deleted" ? e.current?.deletedAt : e.current?.updatedAt,
          deleted: e.kind === "row_deleted",
        };
        setConflict(c);
        setSavedTo(null);
        refresh();
        return { ok: false, text: e.message, conflict: c };
      }
      const text = e instanceof Error ? e.message : String(e);
      setFailed(text);
      setSavedTo(null);
      return { ok: false, text };
    } finally {
      setPending(null);
      setSaving(false);
    }
  };

  return {
    value,
    rows,
    by: latest?.updatedBy,
    at: latest?.updatedAt,
    loading: !!source && (q.isPending || (platform && q.data?.ever === 0 && noteQ.isPending)),
    error: q.isError ? (q.error instanceof Error ? q.error.message : String(q.error)) : null,
    canWrite: q.data?.canWrite ?? false,
    waiting: note ? { rows: note.rows.length, by: note.by, at: note.at } : null,
    bringAcross: async () => {
      if (!note || !source) return { ok: false, text: t("There is nothing to bring across.") };
      try {
        const r = await source.importRegisterRows(siteId, spec.key, note.rows, note.id);
        refresh();
        return { ok: true, text: t("{n} rows brought across. Everyone now saves row by row.", { n: r.imported }) };
      } catch (e) {
        refresh();
        return { ok: false, text: e instanceof Error ? e.message : String(e) };
      }
    },
    save,
    commit: async (next: D, base?: D) => {
      const r = await save(next, base);
      if (!r.ok) throw new Error(r.conflict ? conflictSentence(r.conflict) : r.text);
    },
    saving,
    savedTo,
    failed,
    conflict,
    dismiss: () => {
      setConflict(null);
      setFailed(null);
    },
  };
}
